import {Eq, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AvailableAgentNumber} from 'Users/components/UserAdministration/UserEntry/AgentNumberInput';

const tinRegex = /^\d{9}/;
const formatTIN = (tin: string) =>
  pipe(
    fromNullable(tin.match(/\d/g)),
    foldOption(
      () => '',
      tin => tin.join('')
    )
  );

export class UnvalidatedTaxIdentificationNumber {
  public readonly key = 'UnvalidatedTaxIdentificationNumber';
  public readonly value: string;

  constructor(value: string) {
    this.value = formatTIN(value);
  }
}

export class InvalidTaxIdentificationNumber {
  public readonly key = 'InvalidTaxIdentificationNumber';
  public readonly value: string;

  constructor(value: string) {
    this.value = formatTIN(value);
  }
}

export class ValidatingTaxIdentificationNumber {
  public readonly key = 'ValidatingTaxIdentificationNumber';
  public static create(tin: string) {
    const formattedNumber = formatTIN(tin);

    return tinRegex.test(tin) ? new ValidatingTaxIdentificationNumber(tin) : new InvalidTaxIdentificationNumber(tin);
  }

  private constructor(public readonly value: string) {}
}

export class ExistingTaxIdentificationNumber {
  public readonly key = 'ExistingTaxIdentificationNumber';

  constructor(
    public readonly value: string,
    public readonly encryptedIdentifier: string,
    public readonly availableAgentNumbers: AvailableAgentNumber[]
  ) {}
}

export class ValidTaxIdentificationNumber {
  public readonly value: string;
  public readonly key = 'ValidTaxIdentificationNumber';

  constructor(
    validatingTIN: ValidatingTaxIdentificationNumber,
    public readonly availableAgentNumbers: AvailableAgentNumber[]
  ) {
    this.value = validatingTIN.value;
  }
}

export class TaxIdentificationNumberWithNoAgentNumbers {
  public readonly value: string;
  public readonly key = 'TaxIdentificationNumberWithNoAgentNumbers';

  constructor(validatingTIN: ValidatingTaxIdentificationNumber) {
    this.value = validatingTIN.value;
  }
}

export type TaxIdentificationNumber =
  | InvalidTaxIdentificationNumber
  | ValidTaxIdentificationNumber
  | TaxIdentificationNumberWithNoAgentNumbers
  | ValidatingTaxIdentificationNumber
  | ExistingTaxIdentificationNumber
  | UnvalidatedTaxIdentificationNumber;

type ExhaustiveTaxIdentificationNumberOptions<TReturn> = ToMatchOptions<TaxIdentificationNumber, 'key', TReturn>;
type TaxIdentificationNumberOptionsWithDefault<TReturn> = {
  InvalidTaxIdentificationNumber?: (tin: InvalidTaxIdentificationNumber) => TReturn;
  ValidTaxIdentificationNumber?: (tin: ValidTaxIdentificationNumber) => TReturn;
  ValidatingTaxIdentificationNumber?: (tin: ValidatingTaxIdentificationNumber) => TReturn;
  ExistingTaxIdentificationNumber?: (tin: ExistingTaxIdentificationNumber) => TReturn;
  UnvalidatedTaxIdentificationNumber?: (tin: UnvalidatedTaxIdentificationNumber) => TReturn;
  TaxIdentificationNumberWithNoAgentNumbers?: (tin: TaxIdentificationNumberWithNoAgentNumbers) => TReturn;
  Default: (tin: TaxIdentificationNumber) => TReturn;
};
type TaxIdentificationNumberOptions<TReturn> =
  | ExhaustiveTaxIdentificationNumberOptions<TReturn>
  | TaxIdentificationNumberOptionsWithDefault<TReturn>;

export const matchTaxIdentificationNumber = <TReturn>(
  taxIdNumber: TaxIdentificationNumber,
  options: TaxIdentificationNumberOptions<TReturn>
): TReturn => {
  switch (taxIdNumber.key) {
    case 'InvalidTaxIdentificationNumber':
      return options.InvalidTaxIdentificationNumber
        ? options.InvalidTaxIdentificationNumber(taxIdNumber)
        : (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
    case 'ExistingTaxIdentificationNumber':
      return options.ExistingTaxIdentificationNumber
        ? options.ExistingTaxIdentificationNumber(taxIdNumber)
        : (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
    case 'ValidatingTaxIdentificationNumber':
      return options.ValidatingTaxIdentificationNumber
        ? options.ValidatingTaxIdentificationNumber(taxIdNumber)
        : (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
    case 'TaxIdentificationNumberWithNoAgentNumbers':
      return options.TaxIdentificationNumberWithNoAgentNumbers
        ? options.TaxIdentificationNumberWithNoAgentNumbers(taxIdNumber)
        : (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
    case 'UnvalidatedTaxIdentificationNumber':
      return options.UnvalidatedTaxIdentificationNumber
        ? options.UnvalidatedTaxIdentificationNumber(taxIdNumber)
        : (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
    case 'ValidTaxIdentificationNumber':
      return options.ValidTaxIdentificationNumber
        ? options.ValidTaxIdentificationNumber(taxIdNumber)
        : (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
    default:
      return (options as TaxIdentificationNumberOptionsWithDefault<TReturn>).Default(taxIdNumber);
  }
};

export const eqTaxIdNumber: Eq<TaxIdentificationNumber> = getStructEq({
  value: eqString,
});
